import { nanoid } from "nanoid";
import { JobAction, JobItemStatus } from "~/interface/Job";
import { RunMode } from "~~/interface/Pos";
import { child, onChildAdded, remove } from "firebase/database";
import _ from "lodash";
import { useServerTime } from "~/modules/core/utils/timestamp";
import dayjs from "dayjs";
import { setDoc, arrayUnion, serverTimestamp } from "firebase/firestore";
import { buildBusinessSessionAppend } from "~~/modules/pos/utils/businessSession";
import { markStoreStatusOpened } from "~~/modules/pos/composables/useStoreStatus";
import { TableStateEventType, type TableStateValue } from "~~/interface/TableStateEvent";

export const usePosStore = defineStore("pos", {
  state: () => {
    return {
      runmode: RunMode.UNASSIGNED,
      loading: false,
      //------zone tables-------------------
      zoneList: [
        //     {
        //     id: 1,
        //     name: "T",
        //   //  name: "นอกร้าน",
        //     value: 50
        //     },
        //     {
        //         id: 1,
        //         name: "A",
        //         value: 50
        //     },
      ],
      tableList: [] as any,
      activeList: [] as any,
      selectedZone: null,
      selectedTable: null,

      uuidHolder: {}, // for click items
      // ผล reduce ของ RTDB tableStatesEvents (ดู stores/tableStatesEvents.ts) — ห้ามแก้ตรง ๆ ให้เรียก action ข้างล่าง
      tableState: {} as Record<string, TableStateValue>,
      // true หลัง onChildAdded ชุดแรกของ tableStatesEvents เข้ามาครบ — ก่อนหน้านั้นยังว่าง
      // ปุ่ม Sync to Cloud รอค่านี้ ไม่งั้นเขียน tableState ว่างทับของจริงบน cloud
      tableStateLoaded: false,

      //------modal_items_editor-------------------
      editModeActive: false, //modal
      editMode: null as JobAction, //modal

      // Easy Mode ใช้ state แยกจาก editMode/editModeActive ของ Advance Mode
      // เพื่อไม่ให้ modal สองชุดชนกัน
      easyAction: null as string | null,
      // หลังแยกบิลเสร็จ ให้แถบเลือกบิลโชว์ค้างไว้ แม้จะยังเหลือบิลเดียว
      easyForceShowBills: false,
      // รายการที่ติ๊กไว้บนพาเนล (เก็บเป็น key ของแถวที่รวมแล้ว ไม่ใช่ uuid)
      // modal ทุกตัวอ่านค่านี้ไปใช้เป็นขอบเขต "เฉพาะที่เลือก"
      easySelectedKeys: [] as Array<string>,
      // โหมดแยกรายการ: สลับคอลัมน์ยอดรวมบนพาเนลเป็นปุ่ม "แยก" รายแถว
      easySplitMode: false,
      editModeTitle: "",
      billFilter: "all",
      grandTotalFilter: 0,
      inputValue: {
        reason: "", //void
        price: 0,
        discountMode: 0,
        discountValue: 0,
        bill: 1,
        newTable: "",
        splitQty: 1,
      },
      nonce: nanoid(),
      // jobId ของ OPEN ที่กด "รับทราบ" การเตือนลืมปิดร้านไปแล้ว ผูกกับรอบ ไม่ใช่ true/false
      // ไม่งั้นกดรับทราบรอบหนึ่งแล้ว รอบถัดไปที่ข้ามวันอีกจะไม่เตือนจนกว่าจะ reload
      understoodCrossDateJobId: null as string | null,
      isBeta: false,
    };
  },

  actions: {
    init() {
      useTableStatesEventsStore().init();
    },
    checkRunMode() {
      // rund mode
      let mode = useRunningMode().getMode() || RunMode.UNASSIGNED;
      this.runmode = mode;
    },
    setRunMode(mode) {
      this.runmode = mode;
      useRunningMode().setMode(mode);
    },
    selectZone(zone, type = "zone") {
      const previousZoneName =
        typeof this.selectedZone === "object"
          ? this.selectedZone?.name
          : this.selectedZone;
      const nextZoneName = typeof zone === "object" ? zone?.name : zone;

      this.selectedZone = zone;

      let list = [];
      if (typeof zone === "object" && zone?.value) {
        _.times(zone.value, (i) => {
          list.push(zone.name + "" + (i + 1));
        });
      }
      this.tableList = list;

      if (previousZoneName !== nextZoneName) {
        this.selectedTable = null;
        this.uuidHolder = {};
      }
    },
    selectAllZone(tableSetting) {
      let list = [];

      for (const eachSetting of tableSetting) {
        _.times(eachSetting.value, (i) => {
          list.push(eachSetting.name + "" + (i + 1));
        });
      }

      this.tableList = list;
    },
    selectActiveTables(table) {
      const jobsStore = useJobsStore();
      let tables = _.keys(jobsStore.tables_v2);

      tables = sortTables(tables);
      const groupedData = tables.reduce((acc, item) => {
        const match = item.match(/^([A-Za-z\s]+)(\d+)$/);
        if (match) {
          const letter = match[1].trim();

          if (!acc[letter]) {
            acc[letter] = [];
          }
          acc[letter].push(item);
        }
        return acc;
      }, {});

      this.activeList = groupedData;
    },
    setUnderstandCrossDate() {
      const openShop = useJobsStore().openShop;
      this.understoodCrossDateJobId = openShop?.jobId || openShop?.timestamp || null;
    },

    getTableListBySetting(tableKeys, tableSetting) {
      let groupedTables = {};

      for (const tableKey of tableKeys) {
        if (!groupedTables[tableKey]) {
          groupedTables[tableKey] = [];
        }
        groupedTables[tableKey].push(tableKey);

        //let zoneId = tableKey.match(/^([A-Za-z\s]+)(\d+)$/);

        // const settingDataWithJob = tableSetting.concat({"name": "JOB"});
        // let setting = settingDataWithJob.find((s) => s.name === zoneId[1]);
        //   if (setting) {
        //       // If setting exists, check if the zoneId is already in groupedTables
        //       if (!groupedTables[setting.name]) {
        //           // If not, create a new entry with an array containing the table data
        //           groupedTables[setting.name] = [];
        //       }
        //       // Push the table data into the corresponding zoneId group
        //       groupedTables[setting.name].push(tableKey);
        //   } else {
        //       // Handle cases where the setting for the zoneId is not found
        //   }
      }

      this.activeList = groupedTables;
    },

    selectTable(table) {
      this.selectedTable = table;
      this.uuidHolder = {};
      this.easyForceShowBills = false;
      this.clearEasySelection();
    },

    // ล้างของที่ติ๊กไว้บนพาเนล เรียกตอนเปลี่ยนโต๊ะและหลังทำ action เสร็จ
    // ไม่งั้นการติ๊กค้างจะไปเป็นขอบเขตของ action ครั้งถัดไปโดยที่ผู้ใช้ไม่ตั้งใจ
    clearEasySelection() {
      this.easySelectedKeys = [];
      this.easySplitMode = false;
    },

    //---
    move(targetTable) {
      const jobsStore = useJobsStore();

      let items = this.uuidHolder;
      items = _.values(items);
      // lodash remove falsty value
      items = _.compact(items);
      // uuidHolder เก็บ object ของรายการไว้ตั้งแต่ตอนคลิก ซึ่งค้างค่าเก่า
      // ถ้าอีกเครื่องแยก (split) บรรทัดนี้ไประหว่างนั้น qty เก่าจะไปทับของจริงตอน replay
      // เช่น 3 แยกเป็น 2+1 แล้วย้ายด้วย qty 3 = กลายเป็น 4 ชิ้น -> อ่านรายการล่าสุดก่อนเสมอ
      items = _.compact(_.map(items, (v) => jobsStore.getItemByUuid(v.uuid)));

      const itemsArray = _.map(items, (v, k) => {
        return {
          uuid: v.uuid,
          productId: v.productId,
          qty: v.qty,
          name: v.name,
        };
      });

      let newTable = this.inputValue.newTable;

      // for summary
      let job = new JobBuilder()
        .withTable([this.selectedTable, newTable])
        .withItems(itemsArray)
        .withAction(JobAction.MOVE)
        .withEmployee(this.getEmployee())

        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )

        .build();

      jobsStore.add(job, true);
      this.uuidHolder = {};
    },
    // reason: MANUAL | PRINT_PREVIEW | PRINT_PREVIEW_MOBILE (เก็บไว้ใน event ให้ย้อนดูได้ว่าล็อคเพราะอะไร)
    lockTable(table, reason = "MANUAL") {
      useTableStatesEventsStore().appendEvent(table, TableStateEventType.LOCK, { reason });
    },
    discount() {
      let mode = this.inputValue.discountMode;
      let value = this.inputValue.discountValue;
      const jobsStore = useJobsStore();

      let items = this.uuidHolder;
      items = _.values(items);
      items = _.compact(items);
      // อ่านรายการล่าสุดแทน object ที่ค้างใน uuidHolder (เหตุผลเดียวกับ move)
      // qty เก่าจะทับของจริงตอน replay: 3 แยกเป็น 2+1 แล้วลดราคาด้วย qty 3 = กลายเป็น 4 ชิ้น
      items = _.compact(_.map(items, (v) => jobsStore.getItemByUuid(v.uuid)));
      items = _.map(items, (v, k) => {
        return {
          uuid: v.uuid,
          productId: v.productId,
          qty: v.qty,
          discountItem: [mode, value],
          // ส่วนลดแบบบาทเปลี่ยนความหมายเป็น "ต่อชิ้น" ตั้งแต่เวอร์ชันนี้
          // ติดธงไว้กับตัวรายการ เพื่อให้ของที่ลดไว้ก่อนหน้า (ไม่มีธง)
          // ยังคิดแบบเดิมคือลดครั้งเดียวทั้งบรรทัด
          // ไม่งั้นพอ replay ใหม่ตอน reload ยอดของโต๊ะที่เปิดค้างจะเด้งทันที
          ...(mode == 1 ? { discountPerUnit: true } : {}),
        };
      });

      // for summary
      let job = new JobBuilder()
        .withTable(this.selectedTable)
        .withItems(items)
        .withEmployee(this.getEmployee())

        .withAction(JobAction.SET_DISCOUNT)
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )
        .build();

      jobsStore.add(job, true);
      this.uuidHolder = {};
    },
    void() {
      let reason = this.inputValue.reason;

      const jobsStore = useJobsStore();

      let items = this.uuidHolder;
      items = _.values(items);
      items = _.compact(items);
      items = _.map(items, (v, k) => {
        return {
          uuid: v.uuid,
          productId: v.productId,
          qty: -v.qty,
          ...v,
        };
      });

      // for summary
      let job = new JobBuilder()
        .withTable(this.selectedTable)
        .withNote(reason + " (by " + this.getEmployee().name + ")")
        .withItems(items)
        .withEmployee(this.getEmployee())
        .withAction(JobAction.VOID)
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )
        .build();

      jobsStore.add(job, true);

      // void จนโต๊ะไม่เหลือรายการ = โต๊ะกลับมาว่าง ต้องปล่อย channel ให้เลือกใหม่ได้
      // ไม่งั้น badge channel จะค้างอยู่ในผังโต๊ะ และลูกค้ารายถัดไปโดนราคาของ channel เดิม
      // (เช็คกับ uuid ที่ยังเหลือเอง เพราะ uuidItems ยังไม่ถูก recompute ตอนนี้)
      const voidedUuids = new Set(_.map(items, "uuid"));
      const remaining = _.filter(
        jobsStore.getUuidItemsByTable(this.selectedTable, JobItemStatus.ACTIVE),
        (item: any) => !voidedUuids.has(item.uuid)
      );
      if (remaining.length == 0) {
        this.clearChannel(this.selectedTable); // sc ของ channel ออกไปด้วย
      }

      this.uuidHolder = {};
    },
    printPreview(table) {
      let myTable = this.tableState[table];

      //lock
      this.lockTable(table, "PRINT_PREVIEW");

      //print preview
      const jobsStore = useJobsStore();
      const posStore = usePosStore();
      const shopStore = useShopStore();
      const employeePinStore = useEmployeePinStore();
      const settingsStore = useSettingsStore();

      const roundingType = settingsStore.getRoundingType;

      let bill = posStore.billFilter;
      // build Receipt
      let itemsCombined = jobsStore.itemsCombined(posStore.selectedTable, {
        bill,
      });

      // let moneySummary = jobsStore.computeMoneyOneTable(posStore.selectedTable, roundingType)
      let moneySummary = jobsStore.computeMoneyOneTableWithItemsFiltered(
        {
          items: itemsCombined,
        },
        roundingType
      );

      let _payment = {
        paymentList: [],
        received: 0,
        change: 0,
        summary: moneySummary,
        note: myTable?.note || "",
      };

      let guest =
        this.tableState[table].guests || jobsStore.tables[table].guests || 2;

      let preview = new PreviewBuilder()
        .withTable(table)
        .withItems(itemsCombined)
        .withPayment(_payment)
        .withCreatedAt(
          useServerTime()
            .getCorrectedDayjsInstance()
            .format("YYYY-MM-DD HH:mm:ss")
        )
        .withEmployee(employeePinStore.employee)
        .withStoreId(shopStore.storeId)
        .withGuests(guest)
        .withBill(bill == "all" ? undefined : bill)
        .build();

      setTimeout(async () => {
        const printPreview = await new ThermalPrinterReceipt().setPrintPreview(
          preview
        );
        printPreview.print();
      }, 20);

      return this.tableState;
    },
    printPreviewFromMobile(table, employee) {
      const jobsStore = useJobsStore();

      // คำขอจากมือถืออาจมาถึงหลังโต๊ะถูกปิด/ย้ายไปแล้ว หรือก่อนเครื่องนี้โหลด jobs ของโต๊ะนั้น
      // ไม่มีข้อมูลโต๊ะก็ไม่มีอะไรให้พิมพ์ และห้าม lock โต๊ะที่ไม่มีอยู่
      if (!jobsStore.tables?.[table]) {
        console.warn(`printPreviewFromMobile: table "${table}" not found, skip`);
        return this.tableState;
      }

      let myTable = this.tableState[table];

      //lock
      this.lockTable(table, "PRINT_PREVIEW_MOBILE");

      //print preview
      const posStore = usePosStore();
      const shopStore = useShopStore();
      const employeePinStore = useEmployeePinStore();
      const settingsStore = useSettingsStore();

      const roundingType = settingsStore.getRoundingType;

      let bill = posStore.billFilter;
      // build Receipt
      let itemsCombined = jobsStore.itemsCombined(table, { bill });

      let moneySummary = jobsStore.computeMoneyOneTable(table, roundingType);

      let _payment = {
        paymentList: [],
        received: 0,
        change: 0,
        summary: moneySummary,
        note: myTable?.note || "",
      };

      let guest =
        this.tableState[table].guests || jobsStore.tables[table].guests || 2;

      let preview = new PreviewBuilder()
        .withTable(table)
        .withItems(itemsCombined)
        .withPayment(_payment)
        .withCreatedAt(
          useServerTime()
            .getCorrectedDayjsInstance()
            .format("YYYY-MM-DD HH:mm:ss")
        )
        .withEmployee(employee)
        .withStoreId(shopStore.storeId)
        .withGuests(guest)
        .build();

      setTimeout(async () => {
        const printPreview = await new ThermalPrinterReceipt().setPrintPreview(
          preview
        );
        printPreview.print();
      }, 20);

      return this.tableState;
    },
    unlockTable(table) {
      useTableStatesEventsStore().appendEvent(table, TableStateEventType.UNLOCK);
    },
    selectBill(billSelected) {
      this.billFilter = billSelected;
    },
    splitItem(clonedItem) {
      // let newQty = this.inputValue.splitQty
      // newQty = 1

      // let items = this.uuidHolder
      // items = _.values(items)
      // let item = items[0]; // only one item
      // if (items.length >=2 ) {
      //     alert("เลือกสินค้า แยก ได้เพียง 1 รายการเท่านั้น")
      //         return
      // }
      const jobsStore = useJobsStore();

      let newItems = [
        {
          uuid: clonedItem.uuid,
          newUuid: nanoid(4),
          productId: clonedItem.productId,
          qty: -clonedItem.qty,
          table: clonedItem.table,
          value: [clonedItem.originalQty, clonedItem.qty],
        },
      ];

      // for summary
      let job = new JobBuilder()
        .withTable(this.selectedTable)
        .withItems(newItems)
        .withEmployee(this.getEmployee())
        .withAction(JobAction.SPLIT_ITEM)
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )

        .build();

      jobsStore.add(job, true);
      this.uuidHolder = {};
    },
    // แยกหลายบรรทัดในครั้งเดียว ด้วย SPLIT_ITEM job เดียว
    // (JobChainV2.splitItem() วน job.items อยู่แล้ว)
    // ใช้โดย Easy Mode ตอนที่จำนวนที่ผู้ใช้ระบุตัดไม่ลงตัวกับบรรทัดต้นทาง
    // คืนค่า map { uuid ต้นทาง : uuid ใหม่ของส่วนที่แยกออกมา }
    //
    // options.forAction = action ของ Easy Mode ที่เป็นต้นเหตุให้ต้องแยก
    // ใช้แยกแยะใน log ว่าการแยกนี้เป็นเจตนาของลูกค้า (แยกบิล -> ต้องเห็น)
    // หรือเป็นกลไกภายในเพราะจำนวนตัดไม่ลงตัว (void/discount/... -> ซ่อนได้)
    //
    // options.allowFullLine = ยอมให้ "แยก" ทั้งบรรทัด (qty เท่ากับที่มีอยู่)
    // ใช้กับปุ่มแยกรายการ ซึ่งต้องการให้ส่วนที่แยกออกกลายเป็นแถวของตัวเองเสมอ
    // แม้จะพอดีกับบรรทัดต้นทาง (JobChainV2 จะ void บรรทัดเดิมแล้วเหลือ clone ไว้)
    //
    // options.splitGroup = ให้ทุกชิ้นที่แยกในครั้งนี้แสดงรวมเป็นแถวเดียว
    // จำเป็นตอนที่จำนวนที่ขอแยกคร่อมหลายบรรทัดต้นทาง (เช่น 2+2+2+2+2 แยก 4)
    splitItemsBatch(
      list: Array<{ uuid: string; qty: number }>,
      options: {
        forAction?: string;
        allowFullLine?: boolean;
        splitGroup?: string;
      } = {}
    ) {
      const jobsStore = useJobsStore();

      let newUuidBySource: Record<string, string> = {};
      let newItems = _.compact(
        list.map((entry) => {
          let sourceItem = jobsStore.getItemByUuid(entry.uuid);
          if (!sourceItem || entry.qty <= 0) {
            return null;
          }
          const maxQty = options.allowFullLine
            ? sourceItem.qty
            : sourceItem.qty - 1;
          if (entry.qty > maxQty) {
            // ไม่ต้องแยก: ตัดทั้งบรรทัดอยู่แล้ว
            return null;
          }

          let newUuid = nanoid(8);
          newUuidBySource[sourceItem.uuid] = newUuid;

          return {
            uuid: sourceItem.uuid,
            newUuid,
            productId: sourceItem.productId,
            qty: -entry.qty,
            table: sourceItem.table,
            value: [sourceItem.qty, entry.qty],
            ...(options.splitGroup ? { splitGroup: options.splitGroup } : {}),
          };
        })
      );

      if (newItems.length == 0) {
        return newUuidBySource;
      }

      let job = new JobBuilder()
        .withTable(this.selectedTable)
        .withItems(newItems)
        .withEmployee(this.getEmployee())
        .withAction(JobAction.SPLIT_ITEM)
        .withCustom({
          autoSplit: true,
          autoSplitFor: options.forAction || "unknown",
        })
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )
        .build();

      jobsStore.add(job, true);

      return newUuidBySource;
    },
    // แยกบิลแบบกำหนดเลขบิลได้ต่างกันในแต่ละรายการ ภายใน job เดียว
    // (JobChainV2.splitBill เขียนค่า bill ของแต่ละ item ทับลงบรรทัดนั้น ๆ อยู่แล้ว)
    splitBillMap(uuidToBill: Record<string, number>) {
      const jobsStore = useJobsStore();

      let items = _.compact(
        _.map(uuidToBill, (billNo, uuid) => {
          let item = jobsStore.getItemByUuid(uuid);
          if (!item || item.bill == billNo) {
            return null; // ไม่เปลี่ยน ไม่ต้องบันทึก
          }
          return {
            uuid,
            productId: item.productId,
            bill: Number(billNo),
          };
        })
      );

      if (items.length == 0) {
        return 0;
      }

      let job = new JobBuilder()
        .withTable(this.selectedTable)
        .withItems(items)
        .withEmployee(this.getEmployee())
        .withAction(JobAction.SPLIT_BILL)
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )
        .build();

      jobsStore.add(job, true);
      this.uuidHolder = {};

      return items.length;
    },
    splitBill() {
      let billNo = this.inputValue.bill;

      const jobsStore = useJobsStore();

      let items = this.uuidHolder;
      items = _.values(items);
      items = _.compact(items);
      items = _.map(items, (v, k) => {
        return {
          uuid: v.uuid,
          productId: v.productId,
          bill: billNo,
        };
      });

      // for summary
      let job = new JobBuilder()
        .withTable(this.selectedTable)
        .withItems(items)
        .withEmployee(this.getEmployee())
        .withAction(JobAction.SPLIT_BILL)
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )

        .build();

      jobsStore.add(job, true);
      this.uuidHolder = {};
    },
    setPrice() {
      let newPrice = this.inputValue.price;

      const jobsStore = useJobsStore();

      let items = this.uuidHolder;
      items = _.values(items);
      items = _.compact(items);
      items = _.map(items, (v, k) => {
        return {
          uuid: v.uuid,
          productId: v.productId,
          price: newPrice,
          name: v.name,
          beforePrice: v.extendedPrice,
        };
      });

      // for summary
      let job = new JobBuilder()
        .withTable(this.selectedTable)
        .withItems(items)
        .withEmployee(this.getEmployee())
        .withAction(JobAction.SET_PRICE)
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )

        .build();

      jobsStore.add(job, true);
      this.uuidHolder = {};
    },
    setCommentItem(itemsSelected: any[], comment: string) {
      const jobsStore = useJobsStore();

      if (!itemsSelected?.length || !this.selectedTable) {
        return;
      }

      let items = itemsSelected.map((item) => {
        return {
          uuid: item.uuid,
          productId: item.productId,
          price: item.price,
          name: item.name,
          comments: comment,
        };
      });

      let job = new JobBuilder()
        .withTable(this.selectedTable)
        .withItems(items)
        .withEmployee(this.getEmployee())
        .withAction(JobAction.SET_COMMENT)
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )
        .build();

      jobsStore.add(job, true);
      this.uuidHolder = {};
    },
    setGuestQty(table, qty) {
      const jobsStore = useJobsStore();

      // for summary
      let job = new JobBuilder()
        .withTable(table)
        .withGuests(qty)
        .withEmployee(this.getEmployee())
        .withAction(JobAction.SET_GUESTS)
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )
        .build();

      this.setGuest(table, qty);
      jobsStore.add(job, true);
      this.uuidHolder = {};
    },
    // businessDate: ส่งมาเมื่อเปิดช่วง 00:00-04:59 ที่ให้คนเลือกวันขายเอง (ดู modules/pos/utils/businessDate.ts)
    openShop(cashInit: number = 0, chosenBusinessDate: string | null = null) {
      const jobsStore = useJobsStore();

      const businessDate = chosenBusinessDate || useServerTime()
        .getCorrectedDayjsInstance()
        .format("YYYY-MM-DD");

      // for summary
      let job = new JobBuilder()
        .withAction(JobAction.OPEN)
        .withTable(null)
        .withEmployee(this.getEmployee())
        .withCustom({
          cashInitalAmount:cashInit,
          nonce: _.random(1, 1000000),
          businessDate,
          shift: nanoid(3),
          computerUuid: useLocalStorage("computerUuid", nanoid(6)).value,
        })
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )

        .build();

      // อ่านก่อน add เพราะหลัง add แล้ว compuet() จะตั้ง openShop ให้ job นี้เอง
      const currentOpenShop = jobsStore.openShop;
      jobsStore.add(job, true);

      // event แรกของรอบใน tableStatesEvents — บอกรอบปัจจุบัน + ให้ทุกเครื่องรู้ว่าโหลดชุดแรกครบ
      useTableStatesEventsStore().appendOpenShop(job);

      // สถานะร้านบน Firestore stores/{storeId} = opened (ยิงแล้วลืม ไม่ throw) ดู composables/useStoreStatus.ts
      markStoreStatusOpened(job);

      // ลงทะเบียนรอบการขาย (append อย่างเดียว ห้ามเขียนทับ) — ดู modules/pos/utils/businessSession.ts
      // ทำเบื้องหลัง ห้าม block การเปิดร้าน ถ้าพลาดผลคือ "ไม่มี doc = ขายได้" เหมือนเดิม
      const append = buildBusinessSessionAppend(job, currentOpenShop);
      setDoc(
        useFirebaseFirestore().businessSessionDocRef(),
        {
          businessDates: arrayUnion(...append.businessDates),
          openJobIds: arrayUnion(...append.openJobIds),
          openedAt: arrayUnion(...append.openedAt),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      ).catch((error) => console.error("openShop: register business session failed", error));

      // ตามเก็บวันที่ปิดร้านแล้วตัดสต็อกไม่สำเร็จ (เน็ตหลุด/แอปถูกปิดกลางคัน)
      // ทำเบื้องหลัง ห้าม block การเปิดร้าน และห้าม throw ออกมา
      useRecoverPosSold()
        .run(useStoreId(), businessDate)
        .then((recovered) => {
          if (recovered.length > 0) {
            console.log("openShop: คำนวณสต็อกย้อนหลังให้แล้ว", recovered);
          }
        })
        .catch((error) => console.error("openShop: recover pos-sold failed", error));
    },
    closeShop(actualCash: number = 0) {
      const jobsStore = useJobsStore();

      // for summary
      let job = new JobBuilder()
        .withAction(JobAction.CLOSE)
        .withTable(null)
        .withEmployee(this.getEmployee())
        .withCustom({
          actualCash: actualCash,
          nonce: _.random(1, 1000000),
          businessDate: useServerTime()
            .getCorrectedDayjsInstance()
            .format("YYYY-MM-DD"),
          shitf: jobsStore?.openShop?.shift || "001",
        })
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )

        .build();

      jobsStore.add(job, true);

      // setTimeout(() => {
      //     this._doCloseShop()
      // }, 1000);
    },
    // ทุก action ด้านล่างเขียน event ลง RTDB tableStatesEvents แล้ว reduce กลับมาเป็น this.tableState
    setGuest(table, qty) {
      useTableStatesEventsStore().appendEvent(table, TableStateEventType.SET_GUESTS, {
        payload: { guests: qty },
      });
    },
    clearGuest(table) {
      if (this.tableState[table]?.guests == undefined) return;
      useTableStatesEventsStore().appendEvent(table, TableStateEventType.CLEAR_GUESTS);
    },
    setNote(table, note) {
      if (!note) return this.clearNote(table);
      useTableStatesEventsStore().appendEvent(table, TableStateEventType.SET_NOTE, {
        payload: { note },
      });
    },
    clearNote(table) {
      if (this.tableState[table]?.note == undefined) return;
      useTableStatesEventsStore().appendEvent(table, TableStateEventType.CLEAR_NOTE);
    },
    // service charge ของ channel มากับ SET_CHANNEL / CLEAR_CHANNEL ในตัว (ดู utils/tableStateReducer.ts)
    // แทน setServiceChange / clearServiceChange เดิมที่ต้องเรียกคู่กันทุกจุด
    setChannel(table, channel) {
      useTableStatesEventsStore().appendEvent(table, TableStateEventType.SET_CHANNEL, {
        payload: { channel },
      });
    },
    clearChannel(table) {
      if (!this.tableState[table]?.channel) return;
      useTableStatesEventsStore().appendEvent(table, TableStateEventType.CLEAR_CHANNEL);
    },
    // หลาย field ใน event เดียว (เปิดโต๊ะใหม่: แขก + channel) — field ที่เป็น null / undefined ไม่เปลี่ยน
    // jobId = job ที่เปิดโต๊ะ (เก็บเป็น ref ไว้ย้อนดู)
    patchTable(table, fields, jobId?: string) {
      const payload = _.pickBy(_.pick(fields, ["guests", "note", "channel"]), (value) => value != null);
      if (_.isEmpty(payload)) return;
      useTableStatesEventsStore().appendEvent(table, TableStateEventType.PATCH, { payload, ref: jobId });
    },
    getEmployee() {
      const employeePinStore = useEmployeePinStore();
      return {
        employeeId: employeePinStore.employee.employeeId,
        name: employeePinStore.employee.name,
      };
    },
  },
});
